import { Modal, PrimaryButton } from '@systemeio/ui'
import { ConnectInfoIntegration } from 'modules/profile/integrations/components/connect-info-integration'
import { ItemIntegration } from 'modules/profile/integrations/components/item-integration'
import { WrapperIntegration } from 'modules/profile/integrations/components/wrapper-integration'
import { WrapperIntegrationSkeleton } from 'modules/profile/integrations/components/wrapper-integtation-skeleton'
import { useGoogleIntegrations } from 'modules/profile/integrations/google/hooks/use-google-integrations'
import { useIntegrateGoogle } from 'modules/profile/integrations/google/hooks/use-integrate-google'
import {
  IntegrationInterface,
  IntegrationUseCaseEnum,
} from 'modules/profile/integrations/google/types'
import { getHelpPageLinkGoogleSheets } from 'modules/profile/integrations/utils/get-help-page-link-google-sheets'
import { useState } from 'react'
import ConfirmationModal from 'shared/components/confirmation-modal'
import TransLoco from 'shared/components/trans-loco'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useModal } from 'shared/hooks/use-modal'
import useUser from 'shared/hooks/use-user'
import { dateTimeFormat } from 'shared/utils/date-time-format'
import { hasMultipleUseCases } from './utils/has-multiple-use-cases'
import { GoogleSheetsIcon } from '@systemeio/ui/icons'

export const GoogleSheetsIntegrationComingSoon = () => {
  const { t } = useLocoTranslation()
  return (
    <WrapperIntegration
      icon={<GoogleSheetsIcon />}
      title={t('dashboard.user_settings.integrations_page.google_sheets.name')}
      subTitle={t('dashboard.user_settings.integrations_page.google_sheets.subtitle-2')}
      isComingSoon
    />
  )
}

export const GoogleSheetsIntegration = () => {
  const { user } = useUser()
  const { t } = useLocoTranslation()

  const [opened, setOpened] = useState(false)

  const removeModal = useModal<IntegrationInterface>(false, false)

  const { openGoogleLogin, openGoogleLoginToReconnect, isFetching } = useIntegrateGoogle({
    onSuccess: handleIntegrationSuccess,
  })
  const { getIntegrationsByUseCase, removeIntegration, mutate, mutateIntegrationsWithNewItem } =
    useGoogleIntegrations()
  const data = getIntegrationsByUseCase(IntegrationUseCaseEnum.Spreadsheets)

  async function handleIntegrationSuccess(item: IntegrationInterface) {
    await mutateIntegrationsWithNewItem(item)
  }

  async function handleRemove(integration: IntegrationInterface) {
    if (hasMultipleUseCases(integration) && !removeModal.opened) {
      removeModal.open(integration)
      return
    }
    await removeIntegration(integration.id)
    await mutate()
  }

  function handleOpenModal() {
    setOpened(true)
  }

  async function openLogin() {
    await openGoogleLogin(IntegrationUseCaseEnum.Spreadsheets)
    setOpened(false)
  }

  async function handleReconnect(item: IntegrationInterface) {
    await openGoogleLoginToReconnect(item.id, item.googleUserEmail)
    await mutate()
  }

  const isConnected = data?.items.some(integration => !integration.disconnectedAt) ?? false

  return (
    <>
      {data ? (
        <>
          <WrapperIntegration
            icon={<GoogleSheetsIcon />}
            title={t('dashboard.user_settings.integrations_page.google_sheets.name')}
            subTitle={t('dashboard.user_settings.integrations_page.google_sheets.subtitle-2')}
            onClick={handleOpenModal}
            isConnect={isConnected}
          />
          <Modal
            title={t('dashboard.user_settings.integrations_page.google_sheets.title')}
            onClose={() => setOpened(false)}
            opened={opened}
          >
            <>
              {data.items.length !== 0 ? (
                <div className={'flex flex-col'}>
                  <div>
                    {data.items.map(item => (
                      <ItemIntegration
                        key={item.id}
                        email={item.googleUserEmail}
                        status={item.disconnectedAt ? t('global.expired') : t('global.active')}
                        createdAt={dateTimeFormat(item.createdAt, {
                          timeFormat: user?.timeFormat,
                          dateFormat: user?.dateFormat,
                          timeZone: user?.timezone,
                        })}
                        removeIntegration={() => handleRemove(item)}
                        showReconnectButton={!!item.disconnectedAt}
                        reconnectIntegration={() => handleReconnect(item)}
                      />
                    ))}
                  </div>
                  <div className={'flex justify-center'}>
                    <PrimaryButton width={'small'} onClick={openLogin} isFetching={isFetching}>
                      {t('dashboard.user_settings.integrations_page.google_sheets.add_account')}
                    </PrimaryButton>
                  </div>
                </div>
              ) : (
                <ConnectInfoIntegration
                  isFetching={isFetching}
                  href={getHelpPageLinkGoogleSheets(user?.dashboardLocale)}
                  handleOpenIntegrationLogin={openLogin}
                  infoText={
                    'dashboard.user_settings.integrations_page.google_sheets_integration.help'
                  }
                />
              )}
            </>
          </Modal>
        </>
      ) : (
        <WrapperIntegrationSkeleton />
      )}
      <ConfirmationModal
        afterLeave={() => removeModal.cleanData()}
        opened={removeModal.opened}
        onClose={() => removeModal.close()}
        onConfirm={() => removeModal.data && handleRemove(removeModal.data)}
        confirmationText={
          <TransLoco
            t={t}
            i18nKey="dashboard.user_settings.integrations_page.google_sheets.delete_confirmation"
            values={{
              account_address: removeModal.data?.googleUserEmail,
            }}
            components={{
              b: <b />,
            }}
          />
        }
      />
    </>
  )
}
